import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Close, UploadFile } from '@mui/icons-material';
import { getCssVariableHandler } from '../../utils/styles/getCssVariables';
import {
  deleteMyCrewIdentityDocument,
  getCrewIdentityDocument,
  getCrewIdentityDocumentAccessUrl,
  updateMyCrewIdentityDocument,
  uploadMyCrewIdentityDocument,
  type CrewIdentityDocumentMetadata,
  type CrewIdentityDocumentType,
} from '../../api/crewIdentityDocuments';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const DOCUMENT_TYPES: Array<{ value: CrewIdentityDocumentType; label: string }> = [
  { value: 'drivers_license', label: "Driver's License" },
  { value: 'state_id', label: 'State ID' },
  { value: 'passport', label: 'Passport' },
  { value: 'other', label: 'Other Government ID' },
];

export interface CrewIdentityDocumentDraft {
  file: File;
  documentType: CrewIdentityDocumentType;
  expiresOn?: string;
}

// Shown wherever a contractor can upload their ID (Tiffaney, 2026-10-09/12): the file is
// encrypted with its own key and kept outside the app's regular file storage.
const ENCRYPTION_NOTE =
  'Encrypted upload. Your ID is stored privately, separate from the site, and only authorized ShowPhaze staff can view it.';

interface Props {
  contractorId?: string;
  mode?: 'owner' | 'staff';
  hideWhenMissing?: boolean;
  compact?: boolean;
  presentation?: 'card' | 'upload-tile';
  draft?: CrewIdentityDocumentDraft | null;
  onDraftChange?: (draft: CrewIdentityDocumentDraft) => void;
}

function dateInputValue(value?: string | null): string {
  return value ? value.slice(0, 10) : '';
}

function fileIsAllowed(file: File): boolean {
  const extension = file.name.split('.').pop()?.toLowerCase();
  return ['pdf', 'jpg', 'jpeg', 'png', 'heic', 'heif'].includes(extension || '');
}

function errorMessage(failure: unknown, fallback: string): string {
  return failure instanceof Error && failure.message ? failure.message : fallback;
}

export default function GovernmentIdDocumentCard({
  contractorId,
  mode = 'owner',
  hideWhenMissing = false,
  compact = false,
  presentation = 'card',
  draft = null,
  onDraftChange,
}: Props) {
  const isDraftMode = Boolean(onDraftChange);
  const fileInput = useRef<HTMLInputElement>(null);
  const [metadata, setMetadata] = useState<CrewIdentityDocumentMetadata | null>(null);
  const [documentType, setDocumentType] = useState<CrewIdentityDocumentType>('drivers_license');
  const [expiresOn, setExpiresOn] = useState('');
  const [loading, setLoading] = useState(!isDraftMode);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [viewerUrl, setViewerUrl] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const next = await getCrewIdentityDocument(mode === 'staff' ? contractorId : undefined);
      setMetadata(next);
      if (next.documentType) setDocumentType(next.documentType);
      setExpiresOn(dateInputValue(next.expiresOn));
    } catch (requestFailure: unknown) {
      setError(errorMessage(requestFailure, 'Could not load Government ID'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isDraftMode) {
      setLoading(false);
      return;
    }
    if (mode === 'staff' && !contractorId) {
      setLoading(false);
      return;
    }
    void load();
    // Reload when the viewed contractor changes; form state changes do not need a refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractorId, isDraftMode, mode]);

  const openDocument = async () => {
    setWorking(true);
    setError('');
    try {
      const access = await getCrewIdentityDocumentAccessUrl(
        mode === 'staff' ? contractorId : undefined,
      );
      setViewerUrl(access.url);
    } catch (requestFailure: unknown) {
      setError(errorMessage(requestFailure, 'Could not open Government ID'));
    } finally {
      setWorking(false);
    }
  };

  const selectFile = (file: File) => {
    if (!fileIsAllowed(file)) {
      setError('Choose a PDF, JPEG, PNG, or HEIC file.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError('Government ID files must be 10 MB or smaller.');
      return;
    }
    setError('');
    setSelectedFile(file);
  };

  const openEditor = () => {
    setDocumentType(draft?.documentType || metadata?.documentType || 'drivers_license');
    setExpiresOn(draft?.expiresOn || dateInputValue(metadata?.expiresOn));
    setSelectedFile(null);
    setError('');
    setEditorOpen(true);
  };

  const closeEditor = () => {
    if (working) return;
    setEditorOpen(false);
    setSelectedFile(null);
    setError('');
  };

  const saveEditor = async () => {
    const replacing = Boolean(isDraftMode ? draft?.file : metadata?.hasDocument);
    const fileToSave = selectedFile || draft?.file;
    if (!replacing && !fileToSave) {
      setError('Choose a PDF or photo to upload.');
      return;
    }

    const confirmed = selectedFile
      ? window.confirm(
          replacing
            ? `Replace the current Government ID with ${selectedFile.name}?`
            : `Upload ${selectedFile.name} as your Government ID?`,
        )
      : window.confirm('Save these Government ID details?');
    if (!confirmed) return;

    if (isDraftMode && fileToSave) {
      onDraftChange?.({ file: fileToSave, documentType, expiresOn });
      setSelectedFile(null);
      setEditorOpen(false);
      return;
    }

    setWorking(true);
    setError('');
    try {
      const next = selectedFile
        ? await uploadMyCrewIdentityDocument({ file: selectedFile, documentType, expiresOn })
        : await updateMyCrewIdentityDocument({ documentType, expiresOn });
      setMetadata(next);
      if (next.documentType) setDocumentType(next.documentType);
      setExpiresOn(dateInputValue(next.expiresOn));
      setSelectedFile(null);
      setEditorOpen(false);
    } catch (requestFailure: unknown) {
      setError(errorMessage(requestFailure, 'Could not save Government ID'));
    } finally {
      setWorking(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete your Government ID from ShowPhaze? This cannot be undone.')) return;
    setWorking(true);
    setError('');
    try {
      await deleteMyCrewIdentityDocument();
      setMetadata({ hasDocument: false });
      setDocumentType('drivers_license');
      setExpiresOn('');
    } catch (requestFailure: unknown) {
      setError(errorMessage(requestFailure, 'Could not delete Government ID'));
    } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (mode === 'staff' && hideWhenMissing && !metadata?.hasDocument && !error) return null;

  const systemBackground = getCssVariableHandler({ variableName: '--dark-blue-1-rgb' });
  const systemFontColor = getCssVariableHandler({ variableName: '--font-rgb' });
  const viewerIsImage = Boolean(metadata?.contentType?.startsWith('image/'));
  const hasDocument = Boolean(isDraftMode ? draft?.file : metadata?.hasDocument);
  const displayDocumentType = draft?.documentType || metadata?.documentType;

  return (
    <Box
      sx={{
        width: '100%',
        ...(presentation === 'card'
          ? {
              border: '1px solid rgba(255,255,255,0.45)',
              borderRadius: '8px',
              p: compact ? 1.5 : 2,
              mb: 2,
            }
          : {
              display: 'flex',
              alignItems: 'stretch',
              flexDirection: 'column',
            }),
      }}
    >
      {presentation === 'upload-tile' ? (
        <>
          <InputLabel>Government ID (Optional)</InputLabel>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '1rem',
              p: '1rem',
              border: `1px solid ${systemFontColor}`,
              borderRadius: '0.5rem',
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                width: '80px',
                height: '80px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: systemBackground,
                border: `1px solid ${systemFontColor}`,
                borderRadius: '0.5rem',
              }}
            >
              <UploadFile sx={{ color: systemFontColor, fontSize: '2.5rem' }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Button
                className="btn-pink"
                onClick={openEditor}
                disabled={working}
                sx={{ minWidth: 0, px: '2rem', py: '0.5rem' }}
              >
                {hasDocument ? 'Update' : 'Upload'}
              </Button>
              <Typography
                variant={hasDocument ? 'caption' : 'body2'}
                sx={{
                  display: 'block',
                  mt: '0.5rem',
                  color: systemFontColor,
                  opacity: hasDocument ? 1 : 0.7,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {draft?.file?.name ||
                  (hasDocument
                    ? DOCUMENT_TYPES.find((item) => item.value === displayDocumentType)?.label ||
                      'Government ID uploaded'
                    : 'Click to upload PDF or photo')}
              </Typography>
            </Box>
          </Box>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: '#00EEC5' }}>
            {ENCRYPTION_NOTE}
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Government ID
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.75, mb: 2 }}>
            {mode === 'owner'
              ? 'Optional. Upload the front or biographical page as PDF or photo (maximum 10 MB).'
              : 'This contractor has a Government ID on file.'}
          </Typography>
          {mode === 'owner' && (
            // Wording asked by Tiffaney (2026-10-09) so contractors trust the upload.
            <Typography variant="body2" sx={{ color: '#00EEC5', mb: 2 }}>
              {ENCRYPTION_NOTE}
            </Typography>
          )}

          {metadata?.hasDocument && (
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {DOCUMENT_TYPES.find((item) => item.value === metadata.documentType)?.label ||
                'Government ID'}
              {metadata.uploadedAt ? ` · Uploaded ${dateInputValue(metadata.uploadedAt)}` : ''}
              {metadata.expiresOn ? ` · Expires ${dateInputValue(metadata.expiresOn)}` : ''}
            </Typography>
          )}
          {mode === 'owner' && !metadata?.hasDocument && (
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              No Government ID on file.
            </Typography>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            {metadata?.hasDocument && (
              <Button variant="outlined" onClick={openDocument} disabled={working}>
                View
              </Button>
            )}
            {mode === 'owner' && metadata?.hasDocument && (
              <>
                <Button className="btn-pink" onClick={openEditor} disabled={working}>
                  Edit
                </Button>
                <Button color="error" variant="outlined" onClick={remove} disabled={working}>
                  Delete
                </Button>
              </>
            )}
            {mode === 'owner' && !metadata?.hasDocument && (
              <Button className="btn-pink" onClick={openEditor} disabled={working}>
                Upload
              </Button>
            )}
            {working && <CircularProgress size={24} />}
          </Box>
        </>
      )}
      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 1.5 }}>
          {error}
        </Typography>
      )}

      <Modal
        open={editorOpen}
        onClose={closeEditor}
        sx={{
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: 'min(500px, calc(100vw - 32px))',
            maxHeight: 'calc(100vh - 32px)',
            overflowY: 'auto',
            background: systemBackground,
            color: systemFontColor,
            border: `1px solid ${systemFontColor}`,
            borderRadius: '0.5rem',
            boxShadow: 24,
          }}
        >
          <Box
            sx={{
              minHeight: 60,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: '1rem',
              borderBottom: `1px solid ${systemFontColor}`,
            }}
          >
            <Typography variant="h6" sx={{ color: systemFontColor }}>
              {hasDocument ? 'Edit Government ID' : 'Upload Government ID'}
            </Typography>
            <IconButton
              aria-label="Close Government ID editor"
              onClick={closeEditor}
              disabled={working}
              sx={{ color: systemFontColor }}
            >
              <Close />
            </IconButton>
          </Box>

          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" sx={{ color: systemFontColor, opacity: 0.75 }}>
              {hasDocument
                ? 'Update the document type and expiration date. You can also replace the current file.'
                : 'Choose the document type, optional expiration date, and the PDF or photo to upload.'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#00EEC5' }}>
              {ENCRYPTION_NOTE}
            </Typography>

            <FormControl
              fullWidth
              size="small"
              sx={{
                '& .MuiInputLabel-root': { color: systemFontColor, opacity: 0.75 },
                '& .MuiInputLabel-root.Mui-focused': { color: systemFontColor, opacity: 1 },
                '& .MuiOutlinedInput-root': {
                  color: systemFontColor,
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  '& fieldset': { borderColor: systemFontColor, opacity: 0.55 },
                  '&:hover fieldset': { borderColor: systemFontColor, opacity: 0.85 },
                  '&.Mui-focused fieldset': { borderColor: systemFontColor, opacity: 1 },
                },
                '& .MuiSelect-icon': { color: systemFontColor },
              }}
            >
              <InputLabel id="government-id-type-label">Document type</InputLabel>
              <Select
                labelId="government-id-type-label"
                label="Document type"
                value={documentType}
                onChange={(event) =>
                  setDocumentType(event.target.value as CrewIdentityDocumentType)
                }
                MenuProps={{
                  sx: { zIndex: 2100 },
                  PaperProps: {
                    sx: {
                      background: systemBackground,
                      color: systemFontColor,
                      border: `1px solid ${systemFontColor}`,
                      '& .MuiMenuItem-root:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                      '& .MuiMenuItem-root.Mui-selected': {
                        backgroundColor: 'rgba(213,63,202,0.28)',
                      },
                    },
                  },
                }}
              >
                {DOCUMENT_TYPES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              size="small"
              type="date"
              label="Expiration date (optional)"
              value={expiresOn}
              onChange={(event) => setExpiresOn(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiInputLabel-root': { color: systemFontColor, opacity: 0.75 },
                '& .MuiInputLabel-root.Mui-focused': { color: systemFontColor, opacity: 1 },
                '& .MuiOutlinedInput-root': {
                  color: systemFontColor,
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  '& fieldset': { borderColor: systemFontColor, opacity: 0.55 },
                  '&:hover fieldset': { borderColor: systemFontColor, opacity: 0.85 },
                  '&.Mui-focused fieldset': { borderColor: systemFontColor, opacity: 1 },
                },
                '& input::-webkit-calendar-picker-indicator': {
                  cursor: 'pointer',
                  filter: 'invert(1)',
                  opacity: 0.9,
                },
              }}
            />

            <input
              ref={fileInput}
              hidden
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,application/pdf,image/jpeg,image/png,image/heic,image/heif"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) selectFile(file);
              }}
            />
            <Box
              sx={{
                p: 1.5,
                border: `1px dashed ${systemFontColor}`,
                borderRadius: '8px',
                display: 'flex',
                gap: 1.5,
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="body2" sx={{ color: systemFontColor, overflowWrap: 'anywhere' }}>
                {selectedFile?.name ||
                  draft?.file?.name ||
                  (metadata?.hasDocument ? 'Keep the current file' : 'No file selected')}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => fileInput.current?.click()}
                disabled={working}
              >
                {hasDocument ? 'Replace file' : 'Choose file'}
              </Button>
            </Box>

            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" onClick={closeEditor} disabled={working}>
                Cancel
              </Button>
              <Button className="btn-pink" onClick={saveEditor} disabled={working}>
                {working ? <CircularProgress size={20} sx={{ color: systemFontColor }} /> : 'Save'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={Boolean(viewerUrl)}
        onClose={() => setViewerUrl('')}
        sx={{
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: viewerIsImage ? 'fit-content' : 'min(1100px, calc(100vw - 24px))',
            height: viewerIsImage ? 'fit-content' : 'calc(100vh - 24px)',
            maxWidth: 'calc(100vw - 24px)',
            maxHeight: 'calc(100vh - 24px)',
            background: systemBackground,
            color: systemFontColor,
            border: `1px solid ${systemFontColor}`,
            borderRadius: '0.5rem',
            boxShadow: 24,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              height: 60,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: '1rem',
              flexShrink: 0,
              borderBottom: `1px solid ${systemFontColor}`,
            }}
          >
            <Typography variant="h6" sx={{ color: systemFontColor }}>
              Government ID Preview
            </Typography>
            <IconButton
              aria-label="Close Government ID preview"
              onClick={() => setViewerUrl('')}
              sx={{ color: systemFontColor }}
            >
              <Close />
            </IconButton>
          </Box>
          {viewerIsImage ? (
            <img
              src={viewerUrl}
              alt="Government ID Preview"
              referrerPolicy="no-referrer"
              style={{
                display: 'block',
                width: 'auto',
                height: 'auto',
                maxWidth: 'calc(100vw - 26px)',
                maxHeight: 'calc(100vh - 86px)',
                objectFit: 'contain',
              }}
            />
          ) : (
            <iframe
              src={viewerUrl}
              title="Government ID Preview"
              referrerPolicy="no-referrer"
              style={{ width: '100%', flex: 1, minHeight: 0, border: 'none' }}
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
}
